import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldCheck } from 'lucide-react';
import type { Role, UserPublic } from '@container-system/shared';
import { usersApi } from '../../api/users';
import { useAuth } from '../../app/AuthContext';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { Input, Select } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { PageHeader } from '../../components/ui/PageHeader';
import { FullPageSpinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { ApiRequestError } from '../../api/client';
import { formatDateTime } from '../../utils/format';

const ROLE_LABELS: Record<Role, string> = { ADMIN: 'Administrator', OPERATOR: 'Erfassung', VIEWER: 'Nur Lesen' };

export function UsersPage() {
  const { user: me } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['users'], queryFn: usersApi.list });
  const [editing, setEditing] = useState<UserPublic | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserPublic | null>(null);
  const [tokensFor, setTokensFor] = useState<UserPublic | null>(null);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => {
      toast.show('Benutzer gelöscht.', 'success');
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['users'] });
    },
    onError: (err) => toast.show(err instanceof ApiRequestError ? err.message : 'Löschen fehlgeschlagen.', 'error'),
  });

  const columns: Column<UserPublic>[] = [
    { key: 'username', header: 'Benutzername', render: (u) => u.username },
    { key: 'role', header: 'Rolle', render: (u) => <Badge tone={u.role === 'ADMIN' ? 'accent' : 'neutral'}>{ROLE_LABELS[u.role]}</Badge> },
    { key: 'active', header: 'Status', render: (u) => (u.isActive ? <Badge tone="success">Aktiv</Badge> : <Badge tone="danger">Deaktiviert</Badge>) },
    { key: 'lastLogin', header: 'Letzter Login', render: (u) => (u.lastLoginAt ? formatDateTime(u.lastLoginAt) : '—') },
    {
      key: 'actions',
      header: '',
      render: (u) => (
        <div className="flex justify-end gap-2 text-xs font-medium">
          <button className="text-ivory-400 hover:underline" onClick={() => setTokensFor(u)}>
            Sitzungen
          </button>
          <button className="text-accent-400 hover:underline" onClick={() => setEditing(u)}>
            Bearbeiten
          </button>
          {u.id !== me?.id && (
            <button className="text-danger-400 hover:underline" onClick={() => setDeleteTarget(u)}>
              Löschen
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Benutzerverwaltung"
        actions={
          <Button onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" strokeWidth={2} /> Benutzer
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4 text-accent-400" strokeWidth={1.75} /> Alle Benutzer
          </CardTitle>
        </CardHeader>
        <CardBody>{query.isLoading ? <FullPageSpinner /> : <DataTable columns={columns} rows={query.data ?? []} rowKey={(u) => u.id} />}</CardBody>
      </Card>

      {editing && <UserFormModal initial={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
      {tokensFor && <TokensModal user={tokensFor} onClose={() => setTokensFor(null)} />}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Benutzer löschen"
        message={`Benutzer "${deleteTarget?.username}" unwiderruflich löschen?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function UserFormModal({ initial, onClose }: { initial: UserPublic | null; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [username, setUsername] = useState(initial?.username ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<Role>(initial?.role ?? 'OPERATOR');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      if (initial) {
        return usersApi.update(initial.id, { username, role, isActive, password: password || undefined });
      }
      return usersApi.create({ username, password, role });
    },
    onSuccess: () => {
      toast.show('Gespeichert.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiRequestError ? err.message : 'Speichern fehlgeschlagen.'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => mutation.mutate()} loading={mutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Benutzername" value={username} onChange={(e) => setUsername(e.target.value)} required />
        <Input
          label={initial ? 'Neues Passwort (optional)' : 'Passwort'}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint="Mindestens 10 Zeichen"
        />
        <Select label="Rolle" value={role} onChange={(e) => setRole(e.target.value as Role)}>
          <option value="ADMIN">Administrator</option>
          <option value="OPERATOR">Erfassung</option>
          <option value="VIEWER">Nur Lesen</option>
        </Select>
        {initial && (
          <label className="flex items-center gap-2 text-sm text-ivory-300">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Aktiv
          </label>
        )}
        {error && <p className="text-sm text-danger-500">{error}</p>}
      </div>
    </Modal>
  );
}

function TokensModal({ user, onClose }: { user: UserPublic; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ['users', user.id, 'tokens'], queryFn: () => usersApi.tokens(user.id) });
  const revokeMutation = useMutation({
    mutationFn: (tokenId: string) => usersApi.revokeToken(user.id, tokenId),
    onSuccess: () => {
      toast.show('Sitzung beendet.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['users', user.id, 'tokens'] });
    },
  });

  return (
    <Modal open onClose={onClose} title={`Aktive Sitzungen: ${user.username}`}>
      <div className="flex flex-col gap-2">
        {query.data?.length === 0 && <p className="text-sm text-ivory-500">Keine aktiven Sitzungen.</p>}
        {query.data?.map((t) => (
          <div key={t.id} className="flex items-center justify-between rounded-md border border-surface-700 p-2 text-sm">
            <div>
              <p className="text-ivory-200">{t.deviceInfo ?? 'Unbekanntes Gerät'}</p>
              <p className="text-xs text-ivory-500">
                IP {t.ipAddress ?? '—'} · seit {formatDateTime(t.createdAt)}
              </p>
            </div>
            <button className="text-xs text-danger-500 hover:underline" onClick={() => revokeMutation.mutate(t.id)}>
              Beenden
            </button>
          </div>
        ))}
      </div>
    </Modal>
  );
}
